// v2 pine — a Scots pine: a tall slim two-tone trunk (grey-brown below,
// rust-orange up high, with a slight kink), bare near-horizontal branch stubs,
// and an airy crown of separated flattened needle pads staggered to alternate
// sides so daylight shows between them, finished with a small bright top tuft.
// Variants re-lean the trunk, re-tilt the stubs and shift the needle palette
// deterministically. Ported 1:1 from age-of-mcp's v2/tree-pine.ts, using the
// library's shared `tree` (h01, lobe, pick) helpers.
import * as THREE from "three";
import { defineModel, tree, type Part } from "../../lib";

const PALETTES = [
  { dark: "#2c5f2a", mid: "#377330", lite: "#478a3a" },
  { dark: "#28592e", mid: "#336d34", lite: "#41843e" },
  { dark: "#316327", mid: "#3d782e", lite: "#4e9038" },
] as const;

const BARK_LOW = "#5d4630";
const BARK_HIGH = "#a06a44";
const BRANCH = "#7a5638";

function pineParts(v: number): Part[] {
  const seed = 47 + v * 9;
  const lean = (tree.h01(seed, 0) - 0.5) * 0.12;
  const pal = tree.pick(PALETTES, v);
  const parts: Part[] = [
    // two-tone trunk with a kink where the bark turns rusty
    { geometry: new THREE.CylinderGeometry(0.065, 0.095, 0.75, 6),
      color: BARK_LOW, position: [0, 0.375, 0], rotation: [0, 0, lean] },
    { geometry: new THREE.CylinderGeometry(0.042, 0.062, 0.7, 6),
      color: BARK_HIGH, position: [-lean * 0.4, 1.0, 0],
      rotation: [0, 0, lean * 1.5] },
  ];
  // bare branch stubs, near horizontal like an old pine's whorls
  const stubs: [number, number, number, number][] = [
    [-0.12, 1.14, 0.05, 1.15],
    [0.13, 1.3, -0.04, -1.2],
    [-0.09, 1.48, -0.06, 1.05],
  ];
  for (let k = 0; k < stubs.length; k++) {
    const [sx, sy, sz, rz] = stubs[k]!;
    parts.push({
      geometry: new THREE.CylinderGeometry(0.016, 0.026, 0.32, 4),
      color: BRANCH,
      position: [sx, sy + tree.h01(seed, 70 + k) * 0.05, sz],
      rotation: [tree.h01(seed, 74 + k) * 0.5 - 0.25, 0, rz],
    });
  }
  // staggered airy needle pads — air shows between them
  parts.push(tree.lobe(seed, 20, -0.27, 1.3, 0.1, 0.27, pal.dark, 0.5));
  parts.push(tree.lobe(seed, 21, 0.26, 1.46, -0.12, 0.25, pal.mid, 0.5));
  parts.push(tree.lobe(seed, 22, -0.14, 1.62, -0.04, 0.23, pal.mid, 0.5));
  parts.push(tree.lobe(seed, 23, 0.03, 1.74, 0.04, 0.26, pal.lite, 0.55));
  parts.push(tree.lobe(seed, 24, 0, 1.86, 0, 0.13, pal.lite, 0.6));
  return parts;
}

export const treePineV2 = defineModel({
  id: "tree-pine-v2",
  batchId: "fable",
  name: "Tree pine (v2)",
  tags: ["scenery","tree","pine","low-poly","age-of-mcp","fable"],
  summary: "Low-poly tree pine.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.46,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => pineParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => pineParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => pineParts(2) },
  ],
  animations: [],
});
